import React, {useContext, useEffect} from "react";
import {Link, NavLink, useLocation} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ScienceIcon from "@mui/icons-material/Science";
import ListIcon from "@mui/icons-material/List";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TranslateIcon from '@mui/icons-material/Translate';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import HomeIcon from '@mui/icons-material/Home';
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import {styled, useTheme} from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import MuiDrawer from "@mui/material/Drawer";
import {Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import io from "socket.io-client";
import LanguageSelector from "./LanguageSelector";
import {logoutKeycloakUser} from "../../utils/auth";
import { fetchData } from "../../utils/api";
import {UserContext} from "../../App";

export default function Sidebar() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();
    // get url location
    const location = useLocation();

    const [showDemoNotification, setShowDemoNotification] = React.useState(true);
    const { datasets, experiments } = React.useContext(UserContext);
    const [runsOpen, setRunsOpen] = React.useState(false);  // State to control the dropdown
    const [mlflowRuns, setMlflowRuns] = React.useState([]);  // State to store the dummy runs
    const { storedUid } = useContext(UserContext); // Fetch stored user ID

    // Drawer Settings
    const drawerWidth = 300;
    const headerRef = React.useRef(null);
    const accountRef = React.useRef(null);
    // Create refs for the header and account sections.
    const [scrollableHeight, setScrollableHeight] = React.useState("auto");

    const openedMixin = () => ({
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: "hidden",
    });

    const closedMixin = () => ({
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: "hidden",
        width: `calc(${theme.spacing(7)} + 1px)`,
        [theme.breakpoints.up("sm")]: {
            width: `calc(${theme.spacing(8)} + 1px)`,
        },
    });

    // Function to fetch the MLflow runs using the real API
    const fetchRuns = () => {
        if (datasets.length > 0 && experiments.length > 0) {
            fetchData(
                `${process.env.REACT_APP_BACKEND_URL}/api/v0/runs-active`
            ).then((data) => {
                setMlflowRuns(data.runs);
            });
        }
    };

    const handleRunsToggle = () => {
        setRunsOpen(!runsOpen);
        if (!runsOpen && datasets.length > 0 && experiments.length > 0) {
            fetchRuns();  // Fetch the runs when the dropdown is opened
        }
    };

    useEffect(() => {
        // Establish a WebSocket connection for the user
        const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
            path: "/socket",
            forceNew: true,
            autoConnect: false,
            timeout: 50000,
            transports: ["websocket", "polling", "flashsocket"],
        });

        socket.connect();

        socket.on("connect", () => {
            // Join a general user room
            socket.emit("create-user-room", { uid: storedUid });

            // Listen for refresh signal
            socket.on("refresh-runs", () => {
                if (runsOpen) {
                    fetchRuns();
                }
            });
        });

        return () => {
            // Cleanup WebSocket on component unmount
            socket.disconnect();
        };
    }, [storedUid, runsOpen]);

    useEffect(() => {
        const updateHeight = () => {
            const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 0;
            const accountHeight = accountRef.current ? accountRef.current.offsetHeight : 0;
            // Subtracting an extra margin (e.g., 16px) if needed.
            const available = window.innerHeight - headerHeight - accountHeight - 300;
            setScrollableHeight(available);
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    const DrawerHeader = styled("div")(() => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
    }));

    const Drawer = styled(MuiDrawer, {
        shouldForwardProp: (prop) => prop !== "open",
    })(({open}) => ({
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...(open && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": {
                width: drawerWidth,
                background: theme.palette.background.default,
                color: theme.palette.text.primary,
                transition: "width .2s ease-in-out",
            },
        }),
        ...(!open && {
            ...closedMixin(theme),

            "& .MuiDrawer-paper": closedMixin(theme),
        }),
        "& .MuiDrawer-paper": {
            paddingTop: theme.spacing(2),
            width: "inherit",
            overflow: "hidden",
            border: "none",
        },
    }));


    const matches = !useMediaQuery('(max-width:1000px)');
    const [accountOpen, setAccountOpen] = React.useState(false);

    const handleLogout = () => {
        logoutKeycloakUser();
    }

    const handleOpenAccount = () => {
        setAccountOpen(!accountOpen);
    }

    const isActiveRoute = (route) => {
        // simple method to check, whether a route is active
        const currentPath = location.pathname;
        // if route is a list, check if current path includes one of the routes
        if (Array.isArray(route)) {
            return route.some((r) => currentPath.includes(r));
        }
        return currentPath.includes(route);
    };

    /* Styles */
    const listItemButtonStyles = {
        minHeight: 48,
        justifyContent: matches ? "initial" : "center",
        px: 2.5,
        mb: 1,
    };

    const listItemIconStyles = {
        minWidth: 0,
        mr: matches ? 2 : "auto",
        pl: 0,
        justifyContent: "center",
    };
    const ListItemTextStyles = {
        opacity: matches ? 1 : 0,
        color: "black",
    };


    return (
        <>
            {process.env.REACT_APP_IS_DEMO === "true" ? (
                <Collapse in={showDemoNotification}>
                    <Alert
                        severity="info"
                        action={
                            <IconButton
                                aria-label="close"
                                onClick={() => {
                                    setShowDemoNotification(false);
                                }}
                            >
                                <CloseIcon fontSize="inherit"/>
                            </IconButton>
                        }
                        sx={{
                            mb: 12,
                            position: "absolute",
                            zIndex: 100,
                            bottom: 0,
                            right: 0,
                        }}
                    >
                        {t("sidebar.DemoNotification")}
                    </Alert>

                </Collapse>
            ) : (
                ""
            )}


            <div>
                {!matches ? (
                    <Drawer
                        variant="permanent"
                        open={matches}
                        className={`dock-open-${matches}`}

                    >
                        <Grid
                            sx={{
                                padding: theme.spacing(0, 0)
                            }}
                        >
                            <List
                                sx={{
                                    padding: theme.spacing(2, 1)
                                }}
                            >
                                <ListItem>
                                    <NavLink to="/">
                                        <ListItemIcon sx={listItemIconStyles}>
                                            <HomeIcon/>
                                        </ListItemIcon>
                                    </NavLink>
                                </ListItem>
                                <ListItem>
                                    <NavLink to="quality-check">
                                        <ListItemIcon sx={listItemIconStyles}>
                                            <ScienceIcon/>
                                        </ListItemIcon>
                                    </NavLink>
                                </ListItem>
                                <ListItem>
                                    <NavLink to="overview">
                                        <ListItemIcon sx={listItemIconStyles}>
                                            <ListIcon/>
                                        </ListItemIcon>
                                    </NavLink>
                                </ListItem>
                                <ListItem
                                    key="Account"
                                    className="list-item"
                                    onClick={handleOpenAccount}
                                    selected={isActiveRoute("account")}
                                >
                                    <ListItemIcon sx={listItemIconStyles}>
                                        <PersonOutlineIcon/>
                                    </ListItemIcon>
                                </ListItem>
                                <Collapse in={accountOpen} timeout="auto" unmountOnExit>
                                    <ListItem
                                        key="AccountInfo"
                                        className="list-item sub-item"
                                        disablePadding
                                    >
                                        <Link
                                            to={`${process.env.REACT_APP_KC_SERVER_URL}realms/${process.env.REACT_APP_KC_REALM_ID}/account/?referrer=${process.env.REACT_APP_KC_CLIENT_ID}#/personal-info`}
                                            target="_blank">
                                            <ListItemButton sx={listItemButtonStyles}>
                                                <ListItemText primary={t("sidebar.MyAccountInfo")}/>
                                            </ListItemButton>
                                        </Link>
                                    </ListItem>
                                    <ListItem
                                        key="AccountLogout"
                                        className="list-item sub-item"
                                        onClick={handleLogout}
                                        disablePadding
                                    >
                                        <ListItemButton sx={listItemButtonStyles}>
                                            <ListItemText primary={t("sidebar.MyAccountLogout")}/>
                                        </ListItemButton>
                                    </ListItem>
                                </Collapse>
                            </List>
                        </Grid>
                    </Drawer>
                ) : (
                    <Drawer
                        variant="permanent"
                        open={matches}
                        className={`dock-open-${matches}`}

                    >
                        <List id="sidebar-container">
                            <DrawerHeader ref={headerRef} sx={{justifyContent: "center"}}>
                                <div>
                                    <NavLink to="/" className="logoIcon">
                                        <img
                                            alt="Kilab.EE"
                                            src={theme.logo}
                                            style={{maxHeight: "55px", marginBottom: theme.spacing(2)}}
                                            className="logo"
                                        />
                                    </NavLink>
                                </div>
                            </DrawerHeader>


                            {/*
                                    <ListItem key="Ongoing" className="list-item" selected={isActiveRoute("ongoing")} disablePadding>
                                        <NavLink to="ongoing" sx={{borderRadius:"inherit"}}>
                                            <ListItemButton sx={listItemButtonStyles}>
                                                <ListItemIcon sx={listItemIconStyles}>
                                                    <DirectionsRunIcon/>
                                                </ListItemIcon>
                                                <ListItemText sx={ListItemTextStyles} primary={t('sidebar.OngoingButton')}/>
                                            </ListItemButton>
                                        </NavLink>
                                    </ListItem>
                                    Sub Items Ongoing Runs
                                    DRAFT
                                    <>
                                        <ListItem key="Inspect" className="list-item sub-item" disablePadding
                                                  disabled={currentExperimentProgress?.processed ? false : true}>
                                            <NavLink to="inspect">
                                                 <Stack direction="column" spacing={1} sx={{maxWidth: "200px", marginBottom: theme.spacing(2)}}>
                                                  <Chip
                                                    label="Run: NeuralNetwork"
                                                    icon={<HourglassBottomIcon />}
                                                  />
                                                   <Chip
                                                    label="Processing: SolarData"
                                                    icon={<DirectionsRunIcon />}
                                                  />
                                                    <Chip
                                                    label="Run: Regression"
                                                    icon={<DirectionsRunIcon />}
                                                  />
                                                 <Chip
                                                    label="Processing: WindData"
                                                    icon={<CheckCircleIcon />}
                                                  />
                                                  </Stack>
                                            </NavLink>
                                        </ListItem>
                                           </>

                                    <Divider/>
                                    */}
                            <ListItem
                                key="demoDatasets"
                                className="list-item"
                                selected={
                                    isActiveRoute("examples")
                                }
                                disablePadding
                            >
                                <NavLink to="demo-datasets">
                                    <ListItemButton sx={listItemButtonStyles}>
                                        <ListItemIcon sx={listItemIconStyles}>
                                            <PermMediaIcon/>
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={ListItemTextStyles}
                                            primary={t("sidebar.UseCasesButton")}
                                        />
                                    </ListItemButton>
                                </NavLink>
                            </ListItem>

                            <ListItem
                                key="experimentAnalyzeHeader"
                                className="list-item"
                                selected={
                                    isActiveRoute(["quality-check", "inspect", "train", "analyze", "runs"])
                                }
                                disablePadding
                            >
                                <NavLink to="quality-check">
                                    <ListItemButton sx={listItemButtonStyles}>
                                        <ListItemIcon sx={listItemIconStyles}>
                                            <ScienceIcon/>
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={ListItemTextStyles}
                                            primary={t("sidebar.CurrentExperimentButton")}
                                        />
                                    </ListItemButton>
                                </NavLink>

                            </ListItem>
                            <ListItem
                                key="Overview"
                                className="list-item"
                                selected={isActiveRoute("overview")}
                                disablePadding
                            >
                                <NavLink to="overview">
                                    <ListItemButton sx={listItemButtonStyles}>
                                        <ListItemIcon sx={listItemIconStyles}>
                                            <ListIcon/>
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={ListItemTextStyles}
                                            primary={t("sidebar.MyDatasetsButton")}
                                        />
                                    </ListItemButton>
                                </NavLink>
                            </ListItem>
                            <ListItem
                                key="ScheduledAndRunning"
                                className="list-item"
                                disablePadding
                                onClick={handleRunsToggle}
                                sx={{display: "none"}}
                            >

                                 <ListItemButton  sx={listItemButtonStyles}>

                                    <ListItemIcon sx={listItemIconStyles}>
                                        <DirectionsRunIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={ListItemTextStyles}
                                        primary={t("sidebar.ScheduledAndRunningButton")}
                                    />
                                </ListItemButton>
                            </ListItem>

                            <Collapse in={runsOpen} timeout="auto" unmountOnExit>
                                <div style={{ overflowY: 'auto', maxHeight: scrollableHeight }}>
                                    <List component="div" disablePadding>
                                        {mlflowRuns.length === 0 ? (
                                            <ListItem sx={{ pl: 4 }}>
                                                <ListItemText primary="No runs available" />
                                            </ListItem>
                                        ) : (
                                            mlflowRuns.map((run) => (
                                                <ListItem key={run.runName} sx={{ pl: 4 }}>
                                                    <Button
                                                        component={Link}
                                                        to={`/train/${run.datasetName}/${run.experimentName}/pipeline/${run.runName}`}
                                                        variant="outlined"
                                                        color="primary"
                                                        size="small"
                                                        startIcon={<QueryStatsIcon fontSize="small" />}
                                                        sx={{
                                                            width: '100%',
                                                            marginBottom: '8px',
                                                            justifyContent: 'flex-start',
                                                            textTransform: 'none',
                                                            padding: '8px',
                                                            alignItems: 'flex-start',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                            <Typography variant="body1" component="div">
                                                                {run.runName}
                                                            </Typography>
                                                            <Typography variant="caption" component="div" color="textSecondary">
                                                                {run.status}
                                                            </Typography>
                                                        </div>
                                                    </Button>
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                </div>
                            </Collapse>
                            {/* <ListItem key="Export" className="list-item" selected={isActiveRoute("overview")} disabled disablePadding> */}
                            {/*    <NavLink to=""> */}
                            {/*        <ListItemButton sx={listItemButtonStyles}> */}
                            {/*            <ListItemIcon sx={listItemIconStyles}> */}
                            {/*                <GetAppIcon/> */}
                            {/*            </ListItemIcon> */}
                            {/*            <ListItemText sx={ListItemTextStyles} primary={t('sidebar.Export')}/> */}
                            {/*        </ListItemButton> */}
                            {/*    </NavLink> */}
                            {/* </ListItem> */}
                            {/* <Divider/> */}

                        </List>
                        <List
                            ref={accountRef}
                            sx={{
                                position: 'absolute',
                                bottom: '60px',
                                width: '100%'
                            }}
                        >
                            <ListItem
                                key="Account"
                                className="list-item"
                                onClick={handleOpenAccount}
                                selected={isActiveRoute("account")}
                                sx={{
                                    padding: theme.spacing(0, 1),
                                }}
                            >

                                <ListItemButton sx={listItemButtonStyles}>
                                    <ListItemIcon sx={listItemIconStyles}>
                                        <PersonOutlineIcon/>
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={ListItemTextStyles}
                                        primary={t("sidebar.MyAccountButton")}
                                    />
                                    {/* eslint-disable-next-line no-nested-ternary */}
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={accountOpen} timeout="auto" unmountOnExit>
                                <ListItem
                                    key="AccountInfo"
                                    className="list-item sub-item"
                                    disablePadding
                                >
                                    <Link
                                        to={`${process.env.REACT_APP_KC_SERVER_URL}realms/${process.env.REACT_APP_KC_REALM_ID}/account/?referrer=${process.env.REACT_APP_KC_CLIENT_ID}#/personal-info`}
                                        target="_blank">
                                        <ListItemButton sx={listItemButtonStyles}>
                                            <ListItemText primary={t("sidebar.MyAccountInfo")}/>
                                        </ListItemButton>
                                    </Link>
                                </ListItem>
                                <ListItem
                                    key="AccountLogout"
                                    className="list-item sub-item"
                                    onClick={handleLogout}
                                    disablePadding
                                >
                                    <ListItemButton sx={listItemButtonStyles}>
                                        <ListItemText primary={t("sidebar.MyAccountLogout")}/>
                                    </ListItemButton>
                                </ListItem>
                            </Collapse>
                            <ListItem
                                key="Language"
                                className="list-item"
                                disablePadding
                                sx={{
                                    "&:hover": {
                                        background: "initial"
                                    },
                                }}
                            >
                                <ListItemButton sx={{
                                    ...listItemButtonStyles, marginLeft: theme.spacing(1.5),
                                    "&.MuiListItemButton-root:hover:hover": {
                                        color: "initial", backgroundColor: "initial"
                                    }
                                }}>
                                    <ListItemIcon sx={listItemIconStyles}>
                                        <TranslateIcon/>
                                    </ListItemIcon>
                                    <LanguageSelector/> </ListItemButton>
                            </ListItem>
                        </List>
                        <Grid
                            container
                            spacing={1}
                            id="footer"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid>
                                <Typography align="center">
                                    <Link to="about">{t("sidebar.AboutLink")}</Link>
                                </Typography>
                                <Typography align="center">
                                    <a href="https://www.zsw-bw.de/footer/datenschutz.html"> {t("sidebar.PrivacyLink")}</a> &nbsp;
                                    <a href="https://www.zsw-bw.de/footer/impressum.html"> {t("sidebar.ImprintLink")} </a> &nbsp;
                                    {t("sidebar.Copyright")}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Drawer>
                )}
            </div>
        </>
    );
}
